import { useApiClient, useFetch } from '@marageti/z4-lib';
import { FilterOp, SearchResult } from '@marageti/z4-sdk/lib/entity';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { SalesStage, TripMatch, TripMatchStatus } from '@marageti/z4-sdk/lib/travel';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

function useReminders(options: { assignee?: string } = {}) {
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const searchRequest = useMemo(() => {
    const req: any = {
      filterBy: [
        {
          field: 'status',
          value: [TripMatchStatus.MatchActive],
        },
        { field: 'reminder.index', op: FilterOp.FilterOpGTE, value: ['0'] },
        {
          field: 'salesStage',
          value: [
            SalesStage.FirstMessageSent,
            SalesStage.Consultation,
            SalesStage.ItineraryInReview,
            SalesStage.ItinerarySent,
            SalesStage.ProposalSent,
          ],
        },
      ],
      sortBy: [
        { field: 'reminder.dueAt', order: 'asc' },
      ],
      pagination: { limit: 10000, page: 1 },
    };

    if (options?.assignee) {
      req.filterBy.push({ field: 'assignedTo', value: [options.assignee] });
    }

    return req;
  }, [options?.assignee]);

  const overdueSearchRequest = useMemo(() => ({
    ...searchRequest,
    filterBy: [
      ...searchRequest.filterBy,
      { field: 'reminder.dueAt', op: FilterOp.FilterOpLT, value: ['now'] },
    ],
  }), [searchRequest]);

  const dueTodaySearchRequest = useMemo(() => ({
    ...searchRequest,
    filterBy: [
      ...searchRequest.filterBy,
      {
        field: 'reminder.dueAt',
        op: FilterOp.FilterOpGT,
        value: ['now'],
      },
      {
        field: 'reminder.dueAt',
        op: FilterOp.FilterOpLT,
        value: ['now+1d'],
      },
    ],
  }), [searchRequest]);

  const overDueReminders = useFetch<SearchResult<TripMatch>>(apiClient.tripMatchesClient.postTripMatchSearches, [overdueSearchRequest], true);
  const dueTodayReminders = useFetch<SearchResult<TripMatch>>(apiClient.tripMatchesClient.postTripMatchSearches, [dueTodaySearchRequest], true);

  const dismissReminder = (tripMatchId: string) => {
    apiClient.tripMatchesClient.putTripMatchDismissReminder(tripMatchId)
      .then(() => {
        enqueueSnackbar('Reminder dismissed.', { variant: 'success' });
      })
      .catch((e: ApiError) => {
        if (e.code === 401) navigate('/logout');
        enqueueSnackbar('Reminder dismissal failed.', { variant: 'default' });
      })
      .finally(() => {
        overDueReminders.mutate();
        dueTodayReminders.mutate();
      });
  };

  const snoozeReminder = (tripMatchId: string, selectedDueAt: Date) => {
    const selectedDueAtDatePlus5Seconds = new Date(selectedDueAt.getTime());
    selectedDueAtDatePlus5Seconds.setSeconds(selectedDueAtDatePlus5Seconds.getSeconds() + 5);

    apiClient.tripMatchesClient.putTripMatchSnoozeReminder(tripMatchId, {
      dueAt: selectedDueAtDatePlus5Seconds,
    })
      .then(() => {
        enqueueSnackbar('Reminder snoozed.', { variant: 'success' });
      })
      .catch((e: ApiError) => {
        if (e.code === 401) navigate('/logout');
        enqueueSnackbar('Reminder snoozing failed.', { variant: 'default' });
      })
      .finally(() => {
        overDueReminders.mutate();
        dueTodayReminders.mutate();
      });
  };

  return {
    overDueReminders,
    dueTodayReminders,
    hasReminders: (overDueReminders.data?.hits?.length || 0) > 0 || (dueTodayReminders.data?.hits?.length || 0) > 0,
    dismissReminder,
    snoozeReminder,
  };
}

export default useReminders;
